// Rotate a secret: a new value under the same fields, sealed and signed like a create, in
// the same drawer as adding one.
import { type FormEvent, useState } from "react";
import { Button } from "rizzui";

import { useT } from "../i18n";
import { replaceSecretValue } from "./api";
import { clearValues, formReader } from "./form";
import type { Signer } from "./requests";
import { DrawerBody, DrawerFooter, DrawerHeader, SecretDrawer } from "./SecretDrawer";
import { type Credential, valueShapeOf } from "./templates";
import { ValueInputs } from "./ValueInputs";

interface Props {
  signer: Signer;
  signerName: string;
  /** The secret whose value is replaced; the drawer is closed without one. */
  credential: Credential | undefined;
  onSaved: (credential: Credential) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}

export function ReplaceValueDrawer({ credential, onCancel, ...rest }: Props) {
  const copy = useT().secrets.forms.replace;
  return (
    <SecretDrawer open={!!credential} onClose={onCancel}>
      {credential ? (
        <>
          <DrawerHeader title={copy.title(credential.name)} onClose={onCancel} />
          <ReplaceValueForm key={credential.id} credential={credential} onCancel={onCancel} {...rest} />
        </>
      ) : null}
    </SecretDrawer>
  );
}

export function ReplaceValueForm({ signer, signerName, credential, onSaved, onCancel, onError }: Props & { credential: Credential }) {
  const { common, secrets } = useT();
  const copy = secrets.forms.replace;
  const [busy, setBusy] = useState(false);
  const shape = valueShapeOf(credential);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    setBusy(true);
    try {
      const saved = await replaceSecretValue(signer, credential, shape, formReader(form));
      clearValues(form, shape);
      onSaved(saved);
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      aria-label={copy.form(credential.name)}
      onSubmit={(e) => void onSubmit(e)}
    >
      <DrawerBody>
        <p className="text-[13px] text-gray-500">{copy.lead}</p>
        {credential.kind === "jira_token" && credential.fields.length === 0 ? (
          <p className="rounded-md border border-orange/30 bg-orange/10 px-3 py-2 text-[13px] text-gray-800">{copy.jiraUpgrade}</p>
        ) : null}
        <ValueInputs shape={shape} />
      </DrawerBody>
      <DrawerFooter signedBy={signerName}>
        <Button variant="outline" onClick={onCancel} disabled={busy}>
          {common.actions.cancel}
        </Button>
        <Button type="submit" isLoading={busy}>
          {copy.save}
        </Button>
      </DrawerFooter>
    </form>
  );
}
